/**
 * CustomCursor — un punto amarillo + un anillo magnético que sigue al mouse.
 *
 * - El punto sigue al cursor 1:1 (transform sin re-render React).
 * - El anillo lo persigue con easing → sensación de "trail".
 * - Sobre elementos interactivos (`a`, `button`, `[data-cursor="grow"]`) el anillo
 *   crece y se rellena para feedback de hover.
 * - Se desactiva en touch (`pointer: coarse`) y con `prefers-reduced-motion`.
 * - No oculta el cursor nativo: el efecto convive con él, así que si JS falla
 *   el usuario sigue teniendo cursor.
 */
import { useEffect, useRef } from 'react';

import { useCoarsePointer, useReducedMotion } from '../../hooks/useMediaQuery';

const INTERACTIVE_SELECTOR =
  'a, button, [role="button"], input, textarea, select, label, [data-cursor="grow"]';

export function CustomCursor() {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const isTouch = useCoarsePointer();
  const reduced = useReducedMotion();

  useEffect(() => {
    if (isTouch || reduced) return;

    let mouseX = -100;
    let mouseY = -100;
    let ringX = -100;
    let ringY = -100;
    let rafId = 0;

    const onMove = (e: PointerEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 0) translate(-50%, -50%)`;
        dotRef.current.style.opacity = '1';
      }
    };

    const onLeave = () => {
      if (dotRef.current) dotRef.current.style.opacity = '0';
      if (ringRef.current) ringRef.current.style.opacity = '0';
    };

    const onPointerOver = (e: PointerEvent) => {
      const target = e.target as Element | null;
      const interactive = target?.closest?.(INTERACTIVE_SELECTOR);
      ringRef.current?.classList.toggle('cursor-ring--grow', Boolean(interactive));
    };

    const tick = () => {
      ringX += (mouseX - ringX) * 0.18;
      ringY += (mouseY - ringY) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringX}px, ${ringY}px, 0) translate(-50%, -50%)`;
        ringRef.current.style.opacity = '1';
      }
      rafId = requestAnimationFrame(tick);
    };

    window.addEventListener('pointermove', onMove);
    document.addEventListener('pointerover', onPointerOver);
    document.documentElement.addEventListener('mouseleave', onLeave);
    rafId = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerover', onPointerOver);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafId);
    };
  }, [isTouch, reduced]);

  if (isTouch || reduced) return null;

  return (
    <>
      <div
        ref={dotRef}
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[9999] w-2 h-2 rounded-full bg-etribe-yellow opacity-0 transition-opacity duration-200"
        style={{ mixBlendMode: 'difference' }}
      />
      <div
        ref={ringRef}
        aria-hidden
        className="cursor-ring pointer-events-none fixed top-0 left-0 z-[9998] w-9 h-9 rounded-full border border-etribe-yellow/40 opacity-0 transition-[width,height,border-color,background-color,opacity] duration-200"
      />
    </>
  );
}
